import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./ProductDetails.css";
import { useListByIdQuery } from "../../api/product.api";
import LoadingComponent from "../../common/Loading/Loading.component";
import { toast } from "react-toastify";
import sizeGuide from '../../assets/images/size-guide.jpg'
import { useDispatch } from "react-redux";
import { addItem } from "../../reducer/cart.reducer";
import SEO, { stripHtml } from "../../common/SEO/SEO";

const ProductDetails = ({ toogleCart }) => {
  const params = useParams();
  const dispatch = useDispatch();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false)
  const [selectedSize, setSelectedSize] = useState(""); // State for size

  const [activeIndex, setActiveIndex] = useState(null);         
  const toggleHelp = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  //call the  product api
  const { data, isLoading } = useListByIdQuery(params.id);

  const [selectSize, setSelectSize] = useState();

  const toggleSelectSize = () => {
    setSelectSize(!selectSize);
  };
  

  useEffect(() => {
    if (data) {
      // Backend returns a single product object, not an array
      const productById = data?.result;
      // Verify this is the correct product (by ID or slug)
      if (productById && (productById._id === params.id || productById.slug === params.slug)) {
        setProduct(productById);
      } else {
        setProduct(null);
      }
    }
  }, [data, params.id, params.slug]);
  const size = product?.sizes

  // Filter sizes that have quantity > 0 for display
  const availableSizes = product?.sizes?.filter(size => size.quantity > 0) || [];
  
  // Check if product is in stock (sum of all quantities > 0)
  const totalQuantity = product?.sizes?.reduce((sum, size) => sum + size.quantity, 0) || 0;
  const isStock = totalQuantity > 0;

  // Reset state when product changes (when navigating between different products)
  useEffect(() => {
    setSelectedSize(""); // Reset selected size
    setActiveIndex(null); // Reset accordion state
    window.scrollTo(0, 0); // Reset main window scroll
    
    // Reset scroll position for image container (for mobile horizontal scroll)
    const imageContainer = document.querySelector('.product_img_grid');
    if (imageContainer) {
      imageContainer.scrollLeft = 0; // Reset horizontal scroll (mobile)
    }
  }, [params.id, params.slug]); // Reset when product ID or slug changes

  if (isLoading) return <LoadingComponent 
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f9f9f9',
    }} 
  />;

  if (!product) return <div>Product not found</div>;

  const handleAddToCart = async () => {
    if (!isStock) {
      toast.error("Product is out of stock");
      return;
    }
    if (!selectedSize) {
      toast.error("Please select a size to add the product to the cart");
      return;
    }
    setLoading(true)

    try {
      dispatch(addItem({
        productId: product._id,
        title: product.title,
        slug: product.slug,
        price: product.price,
        productImage: product?.media?.[0]?.url,
        size: selectedSize,
        quantity: 1,
      }));

      toogleCart(); 
    } catch (error) {
      toast.error("Failed to add product to cart.");
    }
    finally{
      setLoading(false)
    }
  };

  const productImages = product?.media?.map((m) => m.url) || [];
  const cleanDescription = stripHtml(product?.description) || `${product?.title || 'Product'} by Maya Wears. High-grade contemporary apparel designed and crafted in Nepal.`;
  const productCanonical = `https://mayawears.com.np/product/${product?.slug || params.slug}/${product?._id || params.id}`;

  const productSchema = product ? {
    "@type": "Product",
    "@id": `${productCanonical}#product`,
    "name": product.title,
    "image": productImages,
    "description": cleanDescription.slice(0, 500),
    "sku": product._id,
    "brand": {
      "@type": "Brand",
      "name": "Maya Wears"
    },
    "offers": {
      "@type": "Offer",
      "url": productCanonical,
      "priceCurrency": "NPR",
      "price": product.price,
      "priceValidUntil": "2027-12-31",
      "itemCondition": "https://schema.org/NewCondition",
      "availability": isStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Maya Wears"
      }
    }
  } : null;

  const breadcrumbSchema = product ? {
    "@type": "BreadcrumbList",
    "@id": `${productCanonical}#breadcrumb`,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://mayawears.com.np"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://mayawears.com.np/all_product"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.title,
        "item": productCanonical
      }
    ]
  } : null;

  return (
    <>
      <SEO
        title={product.title}
        description={cleanDescription}
        canonical={productCanonical}
        ogType="product"
        ogImage={productImages[0]}
        keywords={`${product.title}, Maya Wears, buy ${product.title} Nepal, streetwear Nepal, contemporary clothing Kathmandu`}
        schema={{
          "@context": "https://schema.org",
          "@graph": [productSchema, breadcrumbSchema].filter(Boolean)
        }}
      />
      <div className="product_page">        
        <div className="details_grid">
          <div className="product_img_grid" >
            {product?.media
              ?.slice()
              ?.sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
              ?.map((mediaItem) => (
                mediaItem?.type === 'video' ? (
                  <video
                    key={mediaItem?._id || mediaItem?.url}
                    src={mediaItem?.url}
                    playsInline
                    muted
                    loop
                    autoPlay
                    preload="metadata"
                  />
                ) : (
                  <img
                    key={mediaItem?._id || mediaItem?.url}
                    src={mediaItem?.url}
                    alt={mediaItem?.alt || 'Product media'}
                    loading='lazy'
                  />
                )
              ))}
          </div>
          <div className="product_detail_box" >                
            <div className="product_details maya-editorial-product">
              <h1 className="product__title">{product.title}</h1>
              <p className="price price__off">NRP {product.price}.00</p>
              
              {product.description && (
                <div 
                  className="product__short-desc"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              )}

              <div className="size__color">
                <p className="sizeeee">Select Size</p>
                <div className="size__flex">
                  <div className="size__">
                    {isStock ? (
                        size.map((item,index)=>(
                          <button
                            key={index}
                            disabled={Number(item.quantity) <= 0}
                            className={`size__option  ${
                              selectedSize === item.size ? "activeSize" : ""
                            } ${Number(item.quantity) <= 0 ? "deactvie_size" : ""}`} 
                            onClick={() => setSelectedSize(item.size)}

                          >
                            {item.size}
                          </button>
                        ))
                      // availableSizes.map((item, index) => (
                      //   <button
                      //     key={index}
                      //     className={`size__option  ${
                      //       selectedSize === item.size ? "activeSize" : ""
                      //     }`}
                      //     onClick={() => setSelectedSize(item.size)}
                      //   >
                      //     {item.size}
                      //   </button>
                      // ))
                    ) : (
                      <div className="out-of-stock-message" style={{
                        padding: '10px',
                        backgroundColor: '#f8f8f8',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        color: '#666',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}>
                        Out of Stock
                      </div>
                    )}
                  </div>
                  
                </div>
                <p className="model__wearing__size">
                  <span  >
                    {product.modelSize === 'null' ? '' : product.modelSize }
                  </span>
                </p>
                <p className="size__guideee" onClick={toggleSelectSize} style={{cursor:'pointer'}}>
                    View Size Guide
                  </p>
              </div>
              <div className="buyNow__cartBtn">
                <button
                  className="cart_btn cart__buy hoverBotton maya-luxury-btn"
                  onClick={handleAddToCart}
                  disabled={loading || !isStock}
                  style={{ 
                    cursor: (loading || !isStock) ? 'not-allowed' : 'pointer', 
                    opacity: (loading || !isStock) ? '0.5' : '1' 
                  }}
                >
                  <span className="btn-text">{!isStock ? 'Out of Stock' : 'Add to Cart'}</span>
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
            <div className="desktop_decription">
              <div className={`faq-item ${activeIndex === 0 ? "active" : ""}`} onClick={() => toggleHelp(0)} >
                <div className="faq-question"> <h3> Description </h3> </div>
                <div className="faq-answer">
                  <p dangerouslySetInnerHTML={{ __html: product?.description }}></p>
                </div>
              </div>
              <div className={`faq-item ${activeIndex === 1 ? "active" : ""}`} onClick={() => toggleHelp(1)} >
                <div className="faq-question"> <h3>Fit </h3> </div>
                <div className="faq-answer">
                  <p dangerouslySetInnerHTML={{__html:product?.fit}}></p>
                </div>
              </div>
              <div className={`faq-item ${activeIndex === 2 ? "active" : ""}`} onClick={() => toggleHelp(2)} >
                <div className="faq-question"> <h3>Material and Care </h3> </div>
                <div className="faq-answer">
                  <p dangerouslySetInnerHTML={{__html:product?.materialCare}}></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mobile_decription " style={{width:'95%', margin:'0 auto'}}>
          <div className={`faq-item ${activeIndex === 0 ? "active" : ""}`} onClick={() => toggleHelp(0)} >
            <div className="faq-question"> <h3> Description </h3> </div>
            <div className="faq-answer">
              <p dangerouslySetInnerHTML={{ __html: product?.description }}></p>
            </div>
          </div>
          <div className={`faq-item ${activeIndex === 1 ? "active" : ""}`} onClick={() => toggleHelp(1)} >
            <div className="faq-question"> <h3>Fit </h3> </div>
            <div className="faq-answer">
              <p dangerouslySetInnerHTML={{__html:product?.fit}}></p>
            </div>
          </div>
          <div className={`faq-item ${activeIndex === 2 ? "active" : ""}`} onClick={() => toggleHelp(2)} >
            <div className="faq-question"> <h3>Material and Care </h3> </div>
            <div className="faq-answer">
              <p dangerouslySetInnerHTML={{__html:product?.materialCare}}></p>
            </div>
          </div>
        </div>
        
      </div>
      <div className={`size_popup ${selectSize ? 'dispplay_size_guide': ''}`}>
        <div className="overlay_popup" onClick={toggleSelectSize}></div>
          <div className="sizee_des">
            <img src={sizeGuide} alt="Maya-wears size guide" loading='lazy'/>  
            <button className="close_btnn" onClick={toggleSelectSize}>
              <svg viewBox="0 0 24 24" height='24px' width='24px' xmlns="http://www.w3.org/2000/svg"><g id="Line"><path fill="white" d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,20a9,9,0,1,1,9-9A9,9,0,0,1,12,21Z"/><path fill="white" d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Z"/></g></svg>
            </button>           
          </div>          
      </div> 
      
    </>
  );
};

export default ProductDetails;
